import { Dropdown } from '@clickguard/design-system'
import { ResponsiveBar } from '@nivo/bar'
import { GChartTooltip } from 'components/basic-blocks'
import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState
} from 'react'
import { createPortal } from 'react-dom'
import { RuleActivityWindow, useRuleActivity } from './rule-activity'
import { ReportSection } from './report-section'
import { CustomRule } from './types'
import './rules-activity-chart.css'

type Metric = 'executions' | 'blockedSources'

const METRIC_OPTIONS = [
	{ value: 'executions', label: 'Executions' },
	{ value: 'blockedSources', label: 'Blocked sources' }
]

/** Rules chartable at once — mirrors the production cap. */
export const MAX_CHART_RULES = 5

/** Gap the cursor tooltip keeps from the viewport edges. */
const TIP_MARGIN = 12

/** Hairline each segment extends UP into its neighbour, so no seam opens during a
 *  swap. Upward is safe: nivo emits the stack bottom-first. */
const BAR_BLEED = 2

/** Stand-in for a true zero: nivo parks a zero-value stacked segment on the baseline
 *  instead of at its place in the stack. The tooltip and legend read the raw series. */
const ZERO_STACK_EPSILON = 1e-4

/** The nivo margin and the CSS baseline the bars scale about both come from here. */
const PLOT_HEIGHT = 240
const PLOT_MARGIN = { top: 8, right: 0, bottom: 24, left: 52 }

/** How long each swap animation is armed for, in ms. */
const SWAP_MS = { metric: 420, window: 500 }

/** Spread across however many days are on screen, so both windows sweep at the same
 *  pace. Exported — the rail's mini bars share it. */
export const STAGGER_MS = 140

/** Mutually exclusive on purpose: running both would scale a path that is also
 *  interpolating its `d`. */
type SwapKind = 'metric' | 'window'

// NOT the full categorical set: sea/yellow/rose carry traffic-light semantics.
const CHART_COLORS = [
	'var(--cg-primary-800)', // #3a33ab — deep indigo
	'var(--cg-chart-neon)', // #79c9ff — blue
	'var(--cg-primary-600)', // #554bfd — brand purple
	'var(--cg-chart-pink)', // #ffaff8 — pink
	'rgb(var(--cg-brand-300))' // #b7b2ff — lavender
]

const chartTheme = {
	axis: {
		ticks: {
			text: {
				fill: 'rgb(var(--cg-text-tertiary))',
				fontSize: 11
			}
		},
		legend: {
			text: {
				fill: 'rgb(var(--cg-text-tertiary))',
				fontSize: 11
			}
		}
	},
	grid: {
		line: {
			stroke: 'rgb(var(--cg-border))',
			strokeWidth: 1,
			strokeOpacity: 0.72
		}
	}
}

const seriesFor = (
	window: RuleActivityWindow,
	ruleId: string,
	metric: Metric
): number[] =>
	window.activity.find((activity) => activity.ruleId === ruleId)?.[metric] ||
	window.days.map(() => 0)

// One path with independent top/bottom radii, so the stack reads as ONE bar. The
// arcs are emitted UNCONDITIONALLY, at radius 0 where a corner is square: the CSS
// `d` transition only interpolates paths whose commands match one-for-one.
const roundedColumnPath = (
	x: number,
	y: number,
	w: number,
	h: number,
	rt: number,
	rb: number
): string =>
	`M${x},${y + rt}` +
	`a${rt},${rt} 0 0 1 ${rt},${-rt}` +
	`h${w - rt - rt}` +
	`a${rt},${rt} 0 0 1 ${rt},${rt}` +
	`v${h - rt - rb}` +
	`a${rb},${rb} 0 0 1 ${-rb},${rb}` +
	`h${-(w - rb - rb)}` +
	`a${rb},${rb} 0 0 1 ${-rb},${-rb}` +
	'z'

interface RulesActivityChartProps {
	/** Enabled (toggled-on) rules from the table, capped at MAX_CHART_RULES. */
	selectedRules: CustomRule[]
	/** Header time frame — the chart plots the series' last N days. */
	days: number
}

/** Stacked daily bars fed by the table's Chart checkboxes, in a ReportSection. */
export const RulesActivityChart = ({
	selectedRules,
	days
}: RulesActivityChartProps) => {
	const [metric, setMetric] = useState<Metric>('executions')
	// Armed only for the length of the running swap: a standing `d` transition renders
	// a rule or window toggle as bars sliding sideways past each other. `seq` makes each
	// arming a new object, so a second swap restarts the timer.
	const [swap, setSwap] = useState<{ kind: SwapKind; seq: number } | null>(null)

	const armSwap = useCallback(
		(kind: SwapKind) =>
			setSwap((prev) => ({ kind, seq: (prev?.seq ?? 0) + 1 })),
		[]
	)

	useEffect(() => {
		if (!swap) return
		const timer = setTimeout(() => setSwap(null), SWAP_MS[swap.kind])
		return () => clearTimeout(timer)
	}, [swap])

	const changeMetric = useCallback(
		(next: Metric) => {
			setMetric(next)
			// Same commit as the new `d`, which is what starts the transition.
			armSwap('metric')
		},
		[armSwap]
	)

	// A LAYOUT effect on both counts: arming during render loses the swap (React can
	// drop a render pass, and the second reads the ref the first moved), and a passive
	// effect lands the attribute a commit late, so one finished frame paints first.
	const swappedDays = useRef(days)
	useLayoutEffect(() => {
		if (swappedDays.current === days) return
		swappedDays.current = days
		armSwap('window')
	}, [days, armSwap])

	// Twice the time frame, so the rail's previous-period figure is the same data.
	const activityWindow = useRuleActivity()

	const tipRef = useRef<HTMLDivElement | null>(null)
	const [tipWidth, setTipWidth] = useState(0)

	// Body portal — nivo's own tooltip draws inside the chart container, which the
	// ReportSection's overflow-hidden clips.
	const [hover, setHover] = useState<{
		x: number
		y: number
		day: string
	} | null>(null)

	// Re-measured only when the content can change width, not on every mousemove.
	useLayoutEffect(() => {
		const width = tipRef.current?.getBoundingClientRect().width ?? 0
		setTipWidth((prev) => (Math.abs(prev - width) < 0.5 ? prev : width))
	}, [selectedRules, hover?.day])

	// The header time frame's window over the fetched series.
	const windowDays = useMemo(
		() => activityWindow.days.slice(-days),
		[activityWindow.days, days]
	)
	const axisTicks = useMemo(
		() => [
			windowDays[0],
			windowDays[Math.floor(windowDays.length / 2)],
			windowDays[windowDays.length - 1]
		],
		[windowDays]
	)

	/** True once anything in the window is non-zero: the epsilon above is invisible
	 *  only because a REAL value sets the y-scale far above it. */
	const hasWindowData = useMemo(
		() =>
			selectedRules.some((rule) =>
				seriesFor(activityWindow, rule.id, metric)
					.slice(-days)
					.some((value) => value > 0)
			),
		[selectedRules, metric, days, activityWindow]
	)

	const chartData = useMemo(
		() =>
			windowDays.map((day, dayIndex) => ({
				day,
				...Object.fromEntries(
					selectedRules.map((rule) => [
						// Keyed by id, never by name: `name` is optional and not
						// unique, so two rules sharing one would collapse here.
						rule.id,
						seriesFor(activityWindow, rule.id, metric).slice(-days)[dayIndex] ||
							(hasWindowData ? ZERO_STACK_EPSILON : 0)
					])
				)
			})),
		[selectedRules, metric, days, windowDays, activityWindow, hasWindowData]
	)

	const colorByRule = useMemo(
		() =>
			Object.fromEntries(
				selectedRules.map((rule, index) => [
					rule.id,
					CHART_COLORS[index % CHART_COLORS.length]
				])
			),
		[selectedRules]
	)

	const stackEdges = useMemo(() => {
		const edges: Record<string, { top?: string; bottom?: string }> = {}
		chartData.forEach((day: Record<string, string | number>) => {
			const present = selectedRules
				.map((rule) => rule.id)
				.filter((id) => (day[id] as number) > ZERO_STACK_EPSILON)
			edges[day.day as string] = {
				bottom: present[0],
				top: present[present.length - 1]
			}
		})
		return edges
	}, [chartData, selectedRules])

	// Read through a ref so BarSegment can keep a stable identity — see below.
	const stackEdgesRef = useRef(stackEdges)
	stackEdgesRef.current = stackEdges

	// Delay between one day's bar and the next, likewise behind a ref (see STAGGER_MS).
	const barDelayRef = useRef(0)
	barDelayRef.current =
		windowDays.length > 1 ? STAGGER_MS / (windowDays.length - 1) : 0

	// Custom bar renderer. Identity must NEVER change — React treats a new function as
	// a different component type and remounts the subtree, killing the morph and
	// stranding the tooltip. With the edges behind a ref React just rewrites the `d`.
	const BarSegment = useCallback(({ bar }: any) => {
		const { x, y, color, data } = bar
		// Kept mounted at zero height, or a white hole opens through the bar.
		const width = Math.max(0, bar.width)
		const height = Math.max(0, bar.height)
		const edge = stackEdgesRef.current[data.indexValue as string] || {}
		const r = Math.min(4, height / 2, width / 2)
		const rt = data.id === edge.top ? r : 0
		const rb = 0
		// Skipped for the stack's top (it would grow the bar) and for a
		// collapsed segment (nothing under it to hide the bleed).
		const bleed = height >= 1 && data.id !== edge.top ? BAR_BLEED : 0
		// Whole-day tooltip (all charted rules), cursor-anchored via the portal.
		const show = (event: React.MouseEvent) =>
			setHover({
				x: event.clientX,
				y: event.clientY,
				day: data.indexValue as string
			})
		return (
			<path
				d={roundedColumnPath(x, y - bleed, width, height + bleed, rt, rb)}
				fill={color}
				style={
					{
						'--custom-rules-bar-delay': `${
							(data.index || 0) * barDelayRef.current
						}ms`
					} as React.CSSProperties
				}
				onMouseEnter={show}
				onMouseMove={show}
				onMouseLeave={() => setHover(null)}
			/>
		)
	}, [])

	const metricLabel = metric === 'executions' ? 'executions' : 'blocked sources'

	// Memoized so hover re-renders don't re-render the bars, whose mouseleave
	// listeners then fire reliably.
	const chartBars = useMemo(
		() => (
			<ResponsiveBar
				data={chartData}
				keys={selectedRules.map((rule) => rule.id)}
				indexBy="day"
				// Static render: nivo animates via rAF, which hidden tabs throttle.
				animate={false}
				margin={PLOT_MARGIN}
				padding={0.35}
				colors={({ id }) => colorByRule[id as string]}
				barComponent={BarSegment}
				enableLabel={false}
				enableGridX={false}
				enableGridY
				gridYValues={4}
				// An all-zero window has no maximum, so nivo would invent one.
				maxValue={hasWindowData ? 'auto' : 4}
				theme={chartTheme}
				axisLeft={{
					tickSize: 0,
					tickPadding: 8,
					tickValues: 4,
					legend: `Number of ${metricLabel}`,
					legendPosition: 'middle',
					legendOffset: -44
				}}
				axisBottom={{ tickSize: 0, tickPadding: 8, tickValues: axisTicks }}
				// nivo's own hover layer is off — the BarSegment paths carry their listeners.
				isInteractive={false}
			/>
		),
		[
			chartData,
			selectedRules,
			colorByRule,
			BarSegment,
			axisTicks,
			metricLabel,
			hasWindowData
		]
	)

	return (
		// The tray class hooks the settings-card CSS that remaps --cg-canvas.
		<ReportSection
			className="custom-rules-chart-tray"
			title={`Daily ${metricLabel} by custom rule`}
			info="Executions count rule triggers. Blocked sources count the protective actions created from those rules."
			infoDescription={`Charts the rules you select, up to ${MAX_CHART_RULES}.`}
			headerAction={
				<Dropdown
					options={METRIC_OPTIONS}
					value={metric}
					onValueChange={(value) => changeMetric(value as Metric)}
					// ReportSection's body clips overflow, so the menu must portal out.
					portal
					align="end"
				/>
			}
		>
			<div className="px-6 py-4">
				{selectedRules.length === 0 ? (
					<p className="py-10 text-center text-sm text-[rgb(var(--cg-text-secondary))]">
						Select rules in the table above to chart their daily activity.
					</p>
				) : (
					<>
						<div
							className="custom-rules-chart-plot"
							style={
								{
									height: PLOT_HEIGHT,
									'--custom-rules-chart-baseline': `${
										PLOT_HEIGHT - PLOT_MARGIN.bottom
									}px`
								} as React.CSSProperties
							}
							data-metric-swap={swap?.kind === 'metric' || undefined}
							data-window-swap={swap?.kind === 'window' || undefined}
							aria-label={`Daily ${metricLabel} for ${selectedRules.length} selected rules`}
							role="img"
							// Wrapper-level clear: the per-bar paths are re-created on hover, so a
							// mouseleave pending on a swapped-out path never fires.
							onMouseLeave={() => setHover(null)}
						>
							{chartBars}
						</div>
						{/* Portaled to <body> so the ReportSection's overflow-hidden can't clip it. */}
						{hover &&
							createPortal(
								<div
									ref={tipRef}
									style={{
										position: 'fixed',
										// A fixed box only has `viewport - left` to lay out in.
										left: tipWidth
											? Math.min(
													Math.max(TIP_MARGIN, hover.x - tipWidth / 2),
													window.innerWidth - tipWidth - TIP_MARGIN
											  )
											: hover.x,
										top: hover.y,
										// max-content, so the box is never squeezed on the first hover.
										width: 'max-content',
										transform: tipWidth
											? 'translateY(calc(-100% - 12px))'
											: 'translate(-50%, calc(-100% - 12px))',
										pointerEvents: 'none',
										zIndex: 9999
									}}
								>
									<GChartTooltip
										// The charted day's real date, not a hardcoded year.
										title={
											activityWindow.dayTitles[
												activityWindow.days.indexOf(hover.day)
											] ?? hover.day
										}
										items={[...selectedRules].reverse().map((rule) => ({
											key: rule.id,
											label: rule.name,
											value: (
												seriesFor(activityWindow, rule.id, metric)[
													activityWindow.days.indexOf(hover.day)
												] || 0
											).toLocaleString(),
											color: colorByRule[rule.id]
										}))}
									/>
								</div>,
								document.body
							)}
						<div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5">
							{selectedRules.map((rule) => (
								<span
									key={rule.id}
									className="flex items-center gap-1.5 text-xs text-[rgb(var(--cg-text-secondary))]"
								>
									<span
										aria-hidden
										className="h-2 w-2 rounded-full"
										style={{
											backgroundColor: colorByRule[rule.id]
										}}
									/>
									{rule.name}
								</span>
							))}
						</div>
					</>
				)}
			</div>
		</ReportSection>
	)
}
